import React, { useEffect, useState } from "react";
import {
    Button,
    TextField,
    Select,
    MenuItem,
    Box,
    Typography,
    IconButton,
    Dialog,
    DialogContent,
    FormControl,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
    Stack,
    Checkbox,
    FormControlLabel,
    ClickAwayListener,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { showSnackbar } from "../../../redux/slices/SnackbarSlice";
import {
    listWorkspaceMembersPaginatedAction,
    addWorkspaceMemberAction,
    updateWorkspaceMemberAction,
    listWorkspaceLocationsAction,
} from "../../../redux/actions/WorkspaceActions";
import { menuItemSX } from "../../Builder/BuilderConst";
import { commonSwitchStyles, commonMenuProps } from "../../../theme/CommonStyles";
import { IMemberModalProps } from "./IMemberModal";
import { UserModules } from "../../../redux/slices/AuthSlice2";

const moduleConfig: Record<Exclude<keyof UserModules, 'reselling' | 'dashboard'>, { title: string; description: string }> = {
    rank_tracker: {
        title: "Rank Tracker",
        description: "Track local keyword rankings across your service areas."
    },
    ctr: {
        title: "CTR Booster",
        description: "Improve visibility by simulating real user engagement on your listings."
    },
    map_checkins: {
        title: "Map Check-Ins",
        description: "Post real-world job activity to strengthen local relevance and trust."
    },
    white_label: {
        title: "White Label",
        description: "Customize branding and domain for a fully client-facing experience."
    },
    billing: {
        title: "Billing",
        description: "Manage subscriptions, payments, and account billing settings."
    },
    team: {
        title: "Team Members",
        description: "Invite and manage user access across your workspace."
    },
    // dashboard: {
    //     title: "Dashboard",
    //     description: "View project overview, key metrics, and recent activity."
    // },
    book_call: {
        title: "Book 1-1 Call",
        description: "Schedule a strategy session with our team to optimize your presence."
    }
};

const MemberModal = ({ open, handleClose, member, mode }: IMemberModalProps) => {
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("user");
    const [emailError, setEmailError] = useState("");
    const [modules, setModules] = useState<UserModules>({
        rank_tracker: false,
        ctr: false,
        map_checkins: false,
        white_label: false,
        billing: false,
        team: false,
        dashboard: false,
        book_call: false,
    });
    const [allLocations, setAllLocations] = useState(true);
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [locationSearch, setLocationSearch] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const { addMemberLoading, updateMemberLoading, locations } = useSelector((state: RootState) => state.workspace);
    const isSubmitting = mode === "add" ? addMemberLoading : (updateMemberLoading === member?._id);
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        if (mode === "edit" && member) {
            setEmail(member.userId?.email || "");
            setRole(member.role || "user");
            setEmailError("");
            const modulesData = member.modules || {
                rank_tracker: false,
                ctr: false,
                map_checkins: false,
                white_label: false,
                billing: false,
                team: false,
                dashboard: false,
                book_call: false,
            };
            setModules(modulesData as any);
            setAllLocations(member.allLocations ?? true);
            setSelectedLocations(member.locations?.map((l: any) => l._id) || []);
            setLocationSearch("");
        } else if (mode === "add" && open) {
            setEmail("");
            setRole("user");
            setEmailError("");
            setModules({
                rank_tracker: true,
                ctr: true,
                map_checkins: true,
                white_label: false,
                billing: false,
                team: false,
                dashboard: true,
                book_call: true,
            });
            setAllLocations(true);
            setSelectedLocations([]);
            setLocationSearch("");
        }
    }, [member, mode, open]);

    useEffect(() => {
        if (open) {
            dispatch(listWorkspaceLocationsAction());
        }
    }, [open, dispatch]);

    // Handle role changes during "add" mode to update default modules
    useEffect(() => {
        if (mode === "add" && open) {
            if (role === "admin") {
                setModules({
                    rank_tracker: true,
                    ctr: true,
                    map_checkins: true,
                    white_label: true,
                    billing: true,
                    team: true,
                    dashboard: true,
                    book_call: true,
                });
            } else {
                setModules({
                    rank_tracker: true,
                    ctr: true,
                    map_checkins: true,
                    white_label: false,
                    billing: false,
                    team: false,
                    dashboard: true,
                    book_call: true,
                });
            }
        }
    }, [role, mode, open]);

    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleSubmit = () => {
        if (mode === "add") {
            if (!email) {
                setEmailError("Email is required");
                return;
            }
            if (!validateEmail(email)) {
                setEmailError("Please enter a valid email address");
                return;
            }
        }

        const isAtLeastOneModuleSelected = Object.values(modules).some(val => val === true);
        if (!isAtLeastOneModuleSelected) {
            dispatch(showSnackbar({ message: "Please select at least one module", type: "error" }));
            return;
        }

        let payload: any = { role, modules };

        if (mode === "add") {
            payload = {
                ...payload,
                email,
                allLocations,
                location_ids: allLocations ? [] : selectedLocations
            };
        } else {
            const oldLocationIds = member.locations?.map((l: any) => l._id) || [];
            const add_location_ids = selectedLocations.filter((id: string) => !oldLocationIds.includes(id));
            const remove_location_ids = oldLocationIds.filter((id: string) => !selectedLocations.includes(id));

            payload = {
                ...payload,
                status: member.status,
                allLocations,
                add_location_ids: allLocations ? [] : add_location_ids,
                remove_location_ids: allLocations ? [] : remove_location_ids,
            };
        }

        const actionPromise = mode === "add"
            ? dispatch(addWorkspaceMemberAction(payload))
            : dispatch(updateWorkspaceMemberAction({ id: member._id, data: payload }));

        actionPromise
            .unwrap()
            .then(() => {
                handleClose();
                dispatch(listWorkspaceMembersPaginatedAction({ page: 1, limit: 10, keyword: "", status: "active" }));
            });
    };

    const filteredLocations = (locations || []).filter((loc: any) =>
        loc.business_name?.toLowerCase().includes(locationSearch.toLowerCase()) ||
        loc.address?.toLowerCase().includes(locationSearch.toLowerCase())
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "20px",
                    padding: '8px',
                    maxWidth: '480px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '16px 24px 8px' }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        fontSize: '26px',
                        color: '#111827',
                        fontFamily: 'var(--font-family-manrope)'
                    }}
                >
                    {mode === "add" ? "Invite Workspace Member" : "Update Workspace Member"}
                </Typography>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        color: '#9CA3AF',
                        p: '4px',
                        '&:hover': { bgcolor: '#F3F4F6' }
                    }}
                    size="small"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: '8px 24px 24px' }}>
                <Typography
                    sx={{
                        color: '#4B5563',
                        mb: '20px',
                        fontSize: '15px',
                        lineHeight: 1.5,
                        fontFamily: 'var(--font-family-manrope)'
                    }}
                >
                    {mode === "add"
                        ? "Invite a new member to join your workspace and set their access level."
                        : "Update member role and edit permissions for this workspace."}
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <Typography
                        component="label"
                        sx={{
                            display: 'block',
                            mb: '8px',
                            fontWeight: 600,
                            color: '#374151',
                            fontSize: '14px',
                            fontFamily: 'var(--font-family-manrope)'
                        }}
                    >
                        Email Address
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="example@gmail.com"
                        variant="outlined"
                        size="small"
                        value={email}
                        error={!!emailError}
                        helperText={emailError}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            if (emailError) setEmailError("");
                        }}
                        disabled={mode === "edit"}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                bgcolor: '#fff',
                                '& fieldset': {
                                    borderColor: emailError ? '#dc2626' : '#E5E7EB',
                                },
                                '&:hover fieldset': {
                                    borderColor: emailError ? '#dc2626' : '#D1D5DB'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: emailError ? '#dc2626' : 'var(--action-button-background-color)',
                                },
                            },
                            '& input': {
                                fontSize: '15px',
                                fontFamily: 'var(--font-family-manrope)'
                            },
                            '& .MuiFormHelperText-root': {
                                mx: 0,
                                fontSize: '12px'
                            }
                        }}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography
                        component="label"
                        sx={{
                            display: 'block',
                            mb: '8px',
                            fontWeight: 600,
                            color: '#374151',
                            fontSize: '14px',
                            fontFamily: 'var(--font-family-manrope)'
                        }}
                    >
                        Member Role
                    </Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={role}
                            onChange={(e) => setRole(e.target.value as string)}
                            MenuProps={commonMenuProps}
                            sx={{
                                borderRadius: '12px',
                                color: '#374151',
                                '& .MuiSelect-icon': {
                                    color: '#374151',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#E5E7EB',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#D1D5DB',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--action-button-background-color)',
                                },
                                '& .MuiSelect-select': {
                                    fontSize: '15px',
                                    fontFamily: 'var(--font-family-manrope)',
                                    color: '#374151'
                                }
                            }}
                        >
                            <MenuItem value="user" sx={menuItemSX}>User</MenuItem>
                            <MenuItem value="admin" sx={menuItemSX}>Admin</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Accordion
                    disableGutters
                    elevation={0}
                    square
                    sx={{
                        boxShadow: 'none',
                        border: 'none',
                        '&:before': { display: 'none' }, // remove MUI default divider
                        '&.Mui-expanded': {
                            margin: 0
                        }
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ fontSize: '18px !important', color: '#374151' }} />}
                        sx={{
                            p: 0,
                            minHeight: '40px !important',
                            '& .MuiAccordionSummary-expandIconWrapper': {
                                color: '#374151',
                            },
                            '& .MuiAccordionSummary-content': {
                                margin: '8px 0',
                                '&.Mui-expanded': {
                                    margin: '8px 0'
                                }
                            }
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 600,
                                color: '#374151',
                                fontSize: '14px',
                                fontFamily: 'var(--font-family-manrope)'
                            }}
                        >
                            Edit Permissions
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, pt: 1 }}>
                        <Stack spacing={2.5} sx={{ p: 1 }}>
                            {(Object.keys(moduleConfig) as Array<Exclude<keyof UserModules, 'reselling' | 'dashboard'>>).map((module) => {
                                const config = moduleConfig[module];
                                return (
                                    <React.Fragment key={module}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                opacity: (mode === "edit" ? member?.owner : false) ? 0.6 : 1
                                            }}
                                        >
                                            <Box sx={{ flex: 1, pr: 2 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                        color: '#111827',
                                                        fontFamily: 'var(--font-family-manrope)',
                                                        mb: '2px'
                                                    }}
                                                >
                                                    {config.title}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '13px',
                                                        color: '#6B7280',
                                                        fontFamily: 'var(--font-family-manrope)',
                                                        lineHeight: 1.4
                                                    }}
                                                >
                                                    {config.description}
                                                </Typography>
                                            </Box>
                                            <Switch
                                                size="small"
                                                checked={(modules as any)[module]}
                                                onChange={(e) => {
                                                    const nextModules = { ...modules, [module]: e.target.checked };
                                                    const isAtLeastOneModuleSelected = Object.values(nextModules).some(val => val === true);
                                                    if (!isAtLeastOneModuleSelected) {
                                                        dispatch(showSnackbar({ message: "At least one module must be selected", type: "warning" }));
                                                        return;
                                                    }
                                                    setModules(nextModules);
                                                }}
                                                disabled={mode === "edit" ? member?.owner : false}
                                                sx={commonSwitchStyles}
                                            />
                                        </Box>
                                        {module === 'map_checkins' && modules.map_checkins && (
                                            <Box sx={{ mt: 1, ml: 0 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={!allLocations}
                                                            onChange={(e) => setAllLocations(!e.target.checked)}
                                                            sx={{
                                                                color: '#D1D5DB',
                                                                '&.Mui-checked': {
                                                                    color: 'var(--action-button-background-color)',
                                                                },
                                                                p: '4px'
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography sx={{ fontSize: '13px', color: '#374151', fontWeight: 500, fontFamily: 'var(--font-family-manrope)' }}>
                                                            Limit access to selected locations
                                                        </Typography>
                                                    }
                                                    sx={{ ml: 0, mb: 1 }}
                                                />
                                                {!allLocations && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mt: 0.5, position: 'relative' }}>
                                                        <ClickAwayListener onClickAway={() => setIsDropdownOpen(false)}>
                                                            <Box>
                                                                {/* Selected Tags Box */}
                                                                <Box
                                                                    onClick={() => setIsDropdownOpen(true)}
                                                                    sx={{
                                                                        minHeight: '42px',
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        gap: 1,
                                                                        p: '8px 12px',
                                                                        border: '1px solid #E5E7EB',
                                                                        borderRadius: '12px',
                                                                        bgcolor: '#FFFFFF',
                                                                        cursor: 'text'
                                                                    }}>
                                                                    {locations?.filter((l: any) => selectedLocations.includes(l._id)).map((option: any) => (
                                                                        <Box
                                                                            key={option._id}
                                                                            sx={{
                                                                                px: '10px',
                                                                                py: '3px',
                                                                                bgcolor: '#F3F4F6',
                                                                                borderRadius: '8px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '6px',
                                                                                border: '1px solid #E5E7EB'
                                                                            }}
                                                                        >
                                                                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', fontFamily: 'var(--font-family-manrope)' }}>
                                                                                {option.business_name}
                                                                            </Typography>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setSelectedLocations(selectedLocations.filter(id => id !== option._id));
                                                                                }}
                                                                                sx={{ p: 0, color: '#9CA3AF', '&:hover': { color: '#EF4444' } }}
                                                                            >
                                                                                <CloseIcon sx={{ fontSize: '14px' }} />
                                                                            </IconButton>
                                                                        </Box>
                                                                    ))}
                                                                    <TextField
                                                                        placeholder={selectedLocations.length === 0 ? "Search business name or address..." : "Search..."}
                                                                        variant="standard"
                                                                        fullWidth
                                                                        value={locationSearch}
                                                                        onChange={(e) => {
                                                                            setLocationSearch(e.target.value);
                                                                            if (!isDropdownOpen) setIsDropdownOpen(true);
                                                                        }}
                                                                        onFocus={() => setIsDropdownOpen(true)}
                                                                        InputProps={{
                                                                            disableUnderline: true,
                                                                            sx: {
                                                                                fontSize: '13px',
                                                                                fontFamily: 'var(--font-family-manrope)',
                                                                                px: '4px',
                                                                                py: '4px'
                                                                            }
                                                                        }}
                                                                        sx={{ flex: 1, minWidth: '150px' }}
                                                                    />
                                                                </Box>

                                                                {/* Scrollable List Container */}
                                                                {isDropdownOpen && (
                                                                    <Box sx={{
                                                                        border: '1px solid #E5E7EB',
                                                                        borderRadius: '12px',
                                                                        overflow: 'hidden',
                                                                        bgcolor: '#FFFFFF',
                                                                        position: 'absolute',
                                                                        top: '100%',
                                                                        left: 0,
                                                                        width: '100%',
                                                                        zIndex: 10,
                                                                        mt: 1,
                                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                                                                    }}>
                                                                        <Box sx={{ maxHeight: '300px', overflowY: 'auto', p: 0 }}>
                                                                            {filteredLocations.map((option: any) => {
                                                                                const isSelected = selectedLocations.includes(option._id);
                                                                                return (
                                                                                    <Box
                                                                                        key={option._id}
                                                                                        onClick={() => {
                                                                                            if (isSelected) {
                                                                                                setSelectedLocations(selectedLocations.filter(id => id !== option._id));
                                                                                            } else {
                                                                                                setSelectedLocations([...selectedLocations, option._id]);
                                                                                                setLocationSearch("");
                                                                                            }
                                                                                            setIsDropdownOpen(false);
                                                                                        }}
                                                                                        sx={{
                                                                                            px: '16px',
                                                                                            py: '12px',
                                                                                            display: 'flex',
                                                                                            justifyContent: 'space-between',
                                                                                            alignItems: 'center',
                                                                                            cursor: 'pointer',
                                                                                            borderBottom: '1px solid #F3F4F6',
                                                                                            '&:last-child': { borderBottom: 'none' },
                                                                                            bgcolor: isSelected ? '#F9FAFB' : 'transparent',
                                                                                            '&:hover': { bgcolor: '#F3F4F6' },
                                                                                            transition: 'background-color 0.2s'
                                                                                        }}
                                                                                    >
                                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                                                                            <Typography sx={{
                                                                                                fontSize: '14px',
                                                                                                fontWeight: 600,
                                                                                                color: isSelected ? 'var(--action-button-background-color)' : '#111827',
                                                                                                fontFamily: 'var(--font-family-manrope)'
                                                                                            }}>
                                                                                                {option.business_name}
                                                                                            </Typography>
                                                                                            <Typography sx={{ fontSize: '12px', color: '#6B7280', fontFamily: 'var(--font-family-manrope)' }}>
                                                                                                {option.address}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        {isSelected && <CheckIcon sx={{ color: 'var(--action-button-background-color)', fontSize: '18px' }} />}
                                                                                    </Box>
                                                                                );
                                                                            })}
                                                                            {filteredLocations.length === 0 && (
                                                                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                                                                    <Typography sx={{ fontSize: '14px', color: '#9CA3AF' }}>No locations found</Typography>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </ClickAwayListener>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            </DialogContent>

            <Box sx={{ p: '0 24px 24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        textTransform: 'none',
                        fontSize: '16px',
                        fontFamily: 'var(--font-family-manrope)',
                        fontWeight: 600,
                        px: '24px',
                        py: '8px',
                        borderRadius: '10px',
                        borderColor: '#E0E0E0',
                        backgroundColor: '#ffffff',
                        color: '#374151',
                        '&:hover': {
                            backgroundColor: '#ffffff',
                            borderColor: '#E0E0E0',
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                        textTransform: 'none',
                        fontSize: '16px',
                        fontFamily: 'var(--font-family-manrope)',
                        fontWeight: 600,
                        px: '24px',
                        py: '8px',
                        borderRadius: '10px',
                        backgroundColor: 'var(--action-button-background-color)',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: 'var(--action-button-background-color)',
                            opacity: 0.9,
                            boxShadow: 'none',
                        }
                    }}
                >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (mode === "add" ? "Invite Member" : "Update Access")}
                </Button>
            </Box>
        </Dialog>
    );
};

export default MemberModal;
